// Llamadas: registradas solas desde 3CX y desde el telefono de la Consola. Cada fila abre su ficha (detalle y
// re-tipificacion). "Sin tipificar" y "Perdidas" cuentan lo mismo que el menu y Hoy (data/selectors.ts).
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as crm from '../api/crm';
import type { Llamada } from '../api/types';
import { useSession } from '../auth/session';
import { useAgentes, useLlamadasRecientes, useTipificaciones } from '../data/queries';
import { esContestada, porDevolver, porTipificar } from '../data/selectors';
import { usePhone } from '../hooks';
import { duration, isToday, isYesterday, money, normalize, shortDate, time } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { phone } from '../phone/store';
import { Empty, ErrorBox, Pill, Skeleton } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';
import { sentidoLlamada } from '../ui/LlamadaFicha';
import { toast } from '../ui/toasts';
import { descargarExcel } from './tarificador/csv';

type Ver = 'historial' | 'sintip' | 'perdidas';
type Rango = 'hoy' | '7' | '14' | '30' | '90';
const POR_PAGINA = 100;
const DIA = 86_400_000;

export function Llamadas() {
  const [sp, setSp] = useSearchParams();
  const ver = (['sintip', 'perdidas'].includes(sp.get('ver') ?? '') ? sp.get('ver') : 'historial') as Ver;
  const recientes = useLlamadasRecientes();
  const s = usePhone();
  const { tieneModulo } = useSession();
  const nombres = useAgentes();
  const tips = useTipificaciones();
  const [rango, setRango] = useState<Rango>('7');
  const [agente, setAgente] = useState('');
  const [sentido, setSentido] = useState('');
  const [tip, setTip] = useState('');
  const [q, setQ] = useState('');
  const [visibles, setVisibles] = useState(POR_PAGINA);

  // Hasta 14 dias salen de la consulta que ya comparte todo el CRM; 30 y 90 dias se piden aparte.
  const largo = ver === 'historial' && (rango === '30' || rango === '90');
  const extra = useQuery({
    queryKey: ['llamadas', 'rango', rango],
    queryFn: () => crm.llamadas({ desde: new Date(Date.now() - Number(rango) * DIA), max: 5000 }),
    enabled: largo,
    staleTime: 60_000,
  });
  const r = largo ? extra : recientes;
  const todas = useMemo(() => r.data ?? [], [r.data]);

  // Las mismas definiciones que el menu y Hoy: sin tipificar = tuyas de 7 dias; perdidas = sin devolver en 72 h.
  const ext = s.config.modo === 'demo' ? undefined : s.config.extension || undefined;
  const base = useMemo(() => recientes.data ?? [], [recientes.data]);
  const pend = useMemo(() => porTipificar(base, { extension: ext }), [base, ext]);
  const dev = useMemo(() => porDevolver(base), [base]);
  const agentes = useMemo(() => [...new Set(todas.map((l) => l.agente).filter(Boolean))].sort(), [todas]);

  const lista = useMemo(() => {
    let l: Llamada[] = ver === 'sintip' ? pend : ver === 'perdidas' ? dev : todas;
    if (ver === 'historial') {
      const desde = rango === 'hoy' ? new Date(new Date().setHours(0, 0, 0, 0)).getTime() : Date.now() - Number(rango) * DIA;
      l = l.filter((x) => new Date(x.inicio ?? x.creado).getTime() >= desde);
      if (sentido === 'PERDIDA') l = l.filter((x) => !esContestada(x) && x.direccion === 'ENTRANTE');
      else if (sentido) l = l.filter((x) => x.direccion === sentido);
      if (tip === '-') l = l.filter((x) => !x.tipificacionId && esContestada(x) && x.direccion !== 'INTERNA');
      else if (tip) l = l.filter((x) => x.tipificacionId === tip);
    }
    if (agente) l = l.filter((x) => x.agente === agente);
    const nq = normalize(q.trim());
    const digitos = q.replace(/\D/g, '');
    if (nq)
      l = l.filter(
        (x) =>
          normalize(`${x.persona?.nombre ?? ''} ${x.empresa?.nombre ?? ''} ${x.numero} ${x.tipificacion?.nombre ?? ''} ${nombres.nombre(x.agente)}`).includes(nq) ||
          (!!digitos && x.numero.replace(/\D/g, '').includes(digitos)),
      );
    return l;
  }, [ver, pend, dev, todas, rango, agente, sentido, tip, q, nombres]);

  // Al cambiar un filtro se vuelve a la primera pagina.
  useEffect(() => setVisibles(POR_PAGINA), [ver, rango, agente, sentido, tip, q]);

  const setVer = (v: Ver) => {
    const n = new URLSearchParams(sp);
    if (v === 'historial') n.delete('ver');
    else n.set('ver', v);
    setSp(n, { replace: true });
  };

  const tipificar = (l: Llamada) =>
    phone.abrirCierreExistente({
      llamadaId: l.id,
      numero: l.numero,
      contacto: l.persona ? { id: l.persona.id, nombre: l.persona.nombre, empresa: l.empresa?.nombre } : null,
      dir: l.direccion === 'ENTRANTE' ? 'in' : 'out',
      duracion: l.duracion,
      inicio: new Date(l.inicio ?? l.creado).getTime(),
      notas: l.notas,
    });

  const exportar = () => {
    if (!lista.length) return toast('No hay llamadas para exportar.', 'info');
    const filas = lista.map((l) => {
      const d = new Date(l.inicio ?? l.creado);
      return [
        d.toLocaleDateString('es-CO'),
        d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }),
        sentidoLlamada(l),
        l.numero,
        l.persona?.nombre ?? '',
        l.empresa?.nombre ?? '',
        nombres.conExt(l.agente),
        l.duracion,
        l.costo ? Math.round(l.costo) : '',
        l.costo ? l.moneda : '',
        l.tipificacion?.nombre ?? '',
        l.notas,
      ];
    });
    descargarExcel(`llamadas-${new Date().toISOString().slice(0, 10)}.csv`, [
      ['Fecha', 'Hora', 'Tipo', 'Número', 'Contacto', 'Empresa', 'Agente', 'Duración (s)', 'Costo', 'Moneda', 'Tipificación', 'Notas'],
      ...filas,
    ]);
    toast(`${lista.length} llamadas exportadas.`);
  };

  const cuando = (f: string | null) => (!f ? '—' : isToday(f) ? time(f) : isYesterday(f) ? 'Ayer' : shortDate(f));

  return (
    <div className="view">
      <header className="vhead">
        <div>
          <h1>Llamadas</h1>
          <p className="sub">
            {ver === 'sintip'
              ? 'Tus llamadas contestadas de los últimos 7 días que aún no tienen tipificación'
              : ver === 'perdidas'
                ? 'Entrantes perdidas de las últimas 72 horas que nadie ha devuelto'
                : 'Registradas automáticamente desde 3CX y el teléfono de la Consola · toca una para ver el detalle'}
          </p>
        </div>
        <button className="btn" onClick={exportar} disabled={!lista.length}>
          <Icon name="download" /> Exportar a Excel
        </button>
      </header>
      <div className="tools">
        <div className="seg" role="group" aria-label="Qué ver">
          <button aria-pressed={ver === 'historial'} onClick={() => setVer('historial')}>
            Historial
          </button>
          <button aria-pressed={ver === 'sintip'} onClick={() => setVer('sintip')} data-testid="ver-sintip">
            Sin tipificar · {pend.length}
          </button>
          <button aria-pressed={ver === 'perdidas'} onClick={() => setVer('perdidas')}>
            Perdidas · {dev.length}
          </button>
        </div>
        <label className="field">
          <Icon name="search" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contacto, empresa, número, agente o tipificación" aria-label="Buscar llamadas" />
        </label>
        {ver === 'historial' ? (
          <>
            <select value={rango} onChange={(e) => setRango(e.target.value as Rango)} aria-label="Rango de fechas">
              <option value="hoy">Hoy</option>
              <option value="7">Últimos 7 días</option>
              <option value="14">Últimos 14 días</option>
              <option value="30">Últimos 30 días</option>
              <option value="90">Últimos 90 días</option>
            </select>
            <select value={sentido} onChange={(e) => setSentido(e.target.value)} aria-label="Tipo de llamada">
              <option value="">Todos los tipos</option>
              <option value="ENTRANTE">Entrantes</option>
              <option value="SALIENTE">Salientes</option>
              <option value="PERDIDA">Perdidas</option>
              <option value="INTERNA">Internas</option>
            </select>
            <select value={tip} onChange={(e) => setTip(e.target.value)} aria-label="Tipificación">
              <option value="">Toda tipificación</option>
              <option value="-">Sin tipificar</option>
              {(tips.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <select value={agente} onChange={(e) => setAgente(e.target.value)} aria-label="Agente">
          <option value="">Todos los agentes</option>
          {agentes.map((a) => (
            <option key={a} value={a}>
              {nombres.conExt(a)}
              {a === s.config.extension ? ' (tú)' : ''}
            </option>
          ))}
        </select>
      </div>
      {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
      <section className="box" style={{ padding: '6px 10px' }}>
        {r.isLoading ? (
          <Skeleton rows={10} />
        ) : lista.length ? (
          <div className="rows ctable">
            <div className="row hd">
              <span className="hora">Hora</span>
              <span />
              <span>Contacto</span>
              <span className="ag">Agente</span>
              <span className="r du">Duración</span>
              <span className="r cs">Costo</span>
              <span>Tipificación</span>
            </div>
            {lista.slice(0, visibles).map((l) => {
              const perdida = l.estado !== 'CONTESTADA';
              return (
                <div className="row" key={l.id} data-testid="fila-llamada">
                  <span className="mono hora" style={{ color: 'var(--muted)', fontSize: 12.5 }} title={new Date(l.inicio ?? l.creado).toLocaleString('es-CO')}>
                    {cuando(l.inicio)}
                  </span>
                  <span className={`dir ${perdida ? 'bad' : ''}`} title={sentidoLlamada(l)}>
                    <Icon name={perdida ? 'phoneMiss' : l.direccion === 'ENTRANTE' ? 'phoneIn' : 'phoneOut'} />
                  </span>
                  <button style={{ textAlign: 'left', minWidth: 0 }} onClick={() => abrirDialogo({ tipo: 'verLlamada', id: l.id })} aria-label={`Ver la llamada con ${l.persona?.nombre || formatPhone(l.numero)}`}>
                    <div className="nm" style={perdida && l.direccion === 'ENTRANTE' ? { color: 'var(--bad-ink)' } : undefined}>
                      {l.persona?.nombre || formatPhone(l.numero)}
                    </div>
                    <div className="sb">
                      <span className="solo-movil">{cuando(l.inicio)} · </span>
                      {l.empresa?.nombre || (l.persona ? formatPhone(l.numero) : 'Sin ficha')} · {sentidoLlamada(l)}
                      <span className="solo-movil">{l.duracion ? ` · ${duration(l.duracion)}` : ''}</span>
                    </div>
                  </button>
                  <span className="sb ag" title={l.agente ? `Extensión ${l.agente}` : undefined}>
                    {nombres.nombre(l.agente)}
                  </span>
                  <span className="mono r du">{l.duracion ? duration(l.duracion) : '—'}</span>
                  <span className="mono r cs">{l.costo ? money(l.costo, l.moneda) : '—'}</span>
                  <span>
                    {l.tipificacion ? (
                      <Pill tone={l.gestion === 'SEGUIMIENTO' ? 'warn' : 'acc'}>{l.tipificacion.nombre}</Pill>
                    ) : !perdida && l.duracion > 0 ? (
                      <button className="btn sm" onClick={() => tipificar(l)} disabled={!tieneModulo('TIPIFICACION')}>
                        <Icon name="tag" />
                        Tipificar
                      </button>
                    ) : (
                      <button className="btn sm ghost" data-tel={l.numero} onClick={() => phone.dial(l.numero, l.persona ? { id: l.persona.id, nombre: l.persona.nombre, empresa: l.empresa?.nombre } : null)}>
                        <Icon name="phone" />
                        Devolver
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
            {lista.length > visibles ? (
              <div className="mas">
                <span className="sb">
                  Mostrando {visibles} de {lista.length}
                </span>
                <button className="btn sm" onClick={() => setVisibles((n) => n + POR_PAGINA)}>
                  Mostrar {Math.min(POR_PAGINA, lista.length - visibles)} más
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <Empty icon={ver === 'historial' ? 'list' : 'check'} titulo={ver === 'sintip' ? 'Todo tipificado' : ver === 'perdidas' ? 'Sin llamadas perdidas por devolver' : 'Sin llamadas con estos filtros'} texto={ver === 'historial' ? 'Cuando 3CX o la Consola registren llamadas, aparecerán aquí.' : undefined} />
        )}
      </section>
    </div>
  );
}
